<template>
  <div class="role-edit">
    <header class="page-header">
      <button class="back" @click="$router.back()"><van-icon name="arrow-left"/></button>
      <div class="page-title">{{ isNew ? '新增角色' : '编辑角色' }}</div>
      <button class="save-btn" @click="save">保存</button>
    </header>

    <main class="content">
      <section class="form-section">
        <div class="row">
          <label>角色名称</label>
          <input v-model="form.name" placeholder="如：运营主管"/>
        </div>
        <div class="row">
          <label>数据范围</label>
          <select v-model="form.dataScope">
            <option value="SELF">仅自己</option>
            <option value="DEPARTMENT">本部门</option>
            <option value="DEPARTMENT_AND_CHILDREN">部门及下级</option>
            <option value="TENANT">全体员工</option>
          </select>
        </div>
      </section>

      <section class="perm-section">
        <div class="section-head">
          <b>权限配置（{{ selectedPerms.length }} 项）</b>
          <button class="toggle" @click="toggleAll">{{ allSelected ? '全部清空' : '全部选择' }}</button>
        </div>
        <div v-if="catalogLoading" class="mini-loading"><van-loading size="16px"/>加载权限目录…</div>
        <div v-else-if="!catalog.length" class="mini-empty">暂无权限数据</div>
        <div v-else class="perm-tree">
          <div v-for="mod in catalog" :key="mod.code" class="mod-block">
            <!-- 一级：模块 -->
            <div class="mod-row" @click="toggleModExpand(mod)">
              <span class="expand-arrow" :class="{ expanded: expandedMods[mod.code] }">
                <van-icon name="arrow"/>
              </span>
              <span class="mod-check">
                <van-icon v-if="isModAllSelected(mod)" name="success"/>
                <span v-else-if="isModIndeterminate(mod)" class="indeterminate"/>
                <span v-else class="empty"/>
              </span>
              <span class="mod-label">{{ mod.name }}</span>
              <span class="mod-count">{{ modSelectedCount(mod) }} / {{ modTotalCount(mod) }}</span>
            </div>
            <!-- 二级：资源（展开时显示） -->
            <div v-show="expandedMods[mod.code]" class="resources">
              <div v-for="res in mod.resources" :key="res.code" class="res-block">
                <div class="res-row" @click="toggleResExpand(mod.code, res.code)">
                  <span class="expand-arrow" :class="{ expanded: expandedRes[mod.code + '.' + res.code] }">
                    <van-icon name="arrow"/>
                  </span>
                  <span class="res-check" @click.stop="toggleResAll(mod, res, !isResAllSelected(mod, res))">
                    <van-icon v-if="isResAllSelected(mod, res)" name="success"/>
                    <span v-else-if="isResIndeterminate(mod, res)" class="indeterminate"/>
                    <span v-else class="empty"/>
                  </span>
                  <span class="res-label">{{ res.name }}</span>
                  <span class="res-count">{{ resSelectedCount(mod, res) }} / {{ res.actions.length }}</span>
                </div>
                <!-- 三级：动作（展开时显示） -->
                <div v-show="expandedRes[mod.code + '.' + res.code]" class="actions">
                  <label v-for="act in res.actions" :key="act.code"
                         class="action-item"
                         :class="{ active: selectedPerms.includes(act.code) }"
                         @click="toggleOne(act.code)">
                    <span class="act-check">
                      <van-icon v-if="selectedPerms.includes(act.code)" name="success"/>
                    </span>
                    <span class="act-name">{{ act.name }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import identityApi from '@/api/identity';
import { refreshIdentityCache } from '@/utils/perm';

export default {
  name: 'BossRoleEdit',
  data() {
    return {
      roleId: null,
      isNew: false,
      form: { name: '', dataScope: 'SELF' },
      catalog: [],
      catalogLoading: true,
      selectedPerms: [],
      expandedMods: {},    // { modCode: true }
      expandedRes: {}      // { modCode.resCode: true }
    };
  },
  computed: {
    allSelected() {
      const total = this.catalog.reduce((s, m) =>
        s + m.resources.reduce((a, r) => a + r.actions.length, 0), 0);
      return total > 0 && this.selectedPerms.length === total;
    }
  },
  async created() {
    const { id } = this.$route.params;
    this.roleId = id && id !== 'new' ? Number(id) : null;
    this.isNew = !this.roleId;
    await Promise.all([this.loadCatalog(), this.roleId ? this.loadRole() : Promise.resolve()]);
    // 防御：剔除历史遗留的目录外旧码，避免保存时被后端以 unknown permission code 拒绝
    this.sanitizePerms();
  },
  methods: {
    // —— 加载 ——
    /** 过滤 selectedPerms，仅保留权限目录内的码 */
    sanitizePerms() {
      const codes = new Set(this.catalog.reduce(
        (s, m) => s.concat(...m.resources.map(r => r.actions.map(a => a.code))), []));
      this.selectedPerms = this.selectedPerms.filter(c => codes.has(c));
    },
    async loadCatalog() {
      this.catalogLoading = true;
      try {
        const data = await identityApi.permissionCatalog();
        // API 返回的就是树结构 [{code, name, resources:[{code,name,actions:[{code,name}]}]}]
        this.catalog = Array.isArray(data) ? data : (data.catalog || []);
        // 默认展开全部模块 —— 用 $set 保证 Vue 2 响应式
        for (const m of this.catalog) this.$set(this.expandedMods, m.code, true);
      } catch (e) {
        this.$toast.fail(e.message || '加载权限目录失败');
      } finally {
        this.catalogLoading = false;
      }
    },
    async loadRole() {
      try {
        const role = await identityApi.getRole(this.roleId);
        this.form = {
          name: role.name || '',
          dataScope: role.dataScope || 'SELF'
        };
        this.selectedPerms = (role.permissions || []).slice();
      } catch (e) {
        this.$toast.fail(e.message || '加载角色失败');
      }
    },

    // —— 展开/折叠 ——
    toggleModExpand(mod) {
      this.$set(this.expandedMods, mod.code, !this.expandedMods[mod.code]);
    },
    toggleResExpand(modCode, resCode) {
      const key = modCode + '.' + resCode;
      this.$set(this.expandedRes, key, !this.expandedRes[key]);
    },

    // —— 选中状态查询 ——
    resActionCodes(mod, res) { return res.actions.map(a => a.code); },
    resSelectedCount(mod, res) {
      return this.resActionCodes(mod, res).filter(c => this.selectedPerms.includes(c)).length;
    },
    isResAllSelected(mod, res) {
      const codes = this.resActionCodes(mod, res);
      return codes.length > 0 && codes.every(c => this.selectedPerms.includes(c));
    },
    isResIndeterminate(mod, res) {
      const cnt = this.resSelectedCount(mod, res);
      return cnt > 0 && cnt < res.actions.length;
    },
    modTotalCount(mod) {
      return mod.resources.reduce((s, r) => s + r.actions.length, 0);
    },
    modSelectedCount(mod) {
      return mod.resources.reduce((s, r) => s + this.resSelectedCount(mod, r), 0);
    },
    isModAllSelected(mod) {
      return this.modTotalCount(mod) > 0 && this.modSelectedCount(mod) === this.modTotalCount(mod);
    },
    isModIndeterminate(mod) {
      const cnt = this.modSelectedCount(mod);
      return cnt > 0 && cnt < this.modTotalCount(mod);
    },

    // —— 操作 ——
    toggleResAll(mod, res, checked) {
      const codes = this.resActionCodes(mod, res);
      if (checked) {
        for (const c of codes) if (!this.selectedPerms.includes(c)) this.selectedPerms.push(c);
      } else {
        this.selectedPerms = this.selectedPerms.filter(c => !codes.includes(c));
      }
    },
    toggleOne(code) {
      const i = this.selectedPerms.indexOf(code);
      if (i >= 0) this.selectedPerms.splice(i, 1);
      else this.selectedPerms.push(code);
    },
    toggleAll() {
      const allCodes = [];
      for (const m of this.catalog)
        for (const r of m.resources)
          for (const a of r.actions) allCodes.push(a.code);
      this.selectedPerms = this.allSelected ? [] : allCodes;
    },
    generateCode(name) {
      if (!name || !name.trim()) return 'ROLE_' + Math.random().toString(16).slice(2, 8).toUpperCase();
      const n = name.trim().toUpperCase().replace(/[^A-Z0-9]/g, '_');
      return (n || ('ROLE_' + Date.now().toString().slice(-4))).slice(0, 32);
    },

    // —— 保存 ——
    async save() {
      if (!this.form.name.trim()) { this.$toast.fail('请填写角色名称'); return; }
      try {
        const code = this.generateCode(this.form.name);
        if (this.isNew) {
          const created = await identityApi.createRole({
            name: this.form.name, code, dataScope: this.form.dataScope,
            permissions: this.selectedPerms
          });
          this.roleId = created.id;
          this.isNew = false;
          this.$toast.success('创建成功');
        } else {
          // 名称与权限无依赖，并行提交减少等待
          await Promise.all([
            identityApi.updateRole(this.roleId, {
              name: this.form.name, dataScope: this.form.dataScope
            }),
            identityApi.replacePermissions(this.roleId, this.selectedPerms)
          ]);
          this.$toast.success('保存成功');
        }
        // 若修改的是自己拥有的角色，立即刷新本人权限缓存（否则要等下次进首页才生效）
        await refreshIdentityCache();
        this.$router.back();
      } catch (e) {
        this.$toast.fail(e.message || '保存失败');
      }
    }
  }
};
</script>

<style scoped>
.page-header {
  position: sticky; top: 0; z-index: 10;
  height: 44px; background: #fff;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 12px; border-bottom: 1px solid #eee;
}
.back, .save-btn { background: none; border: 0; font-size: 14px; }
.save-btn { color: #1989fa; font-weight: 500; }
.page-title { font-weight: 500; }
.content { padding: 12px 16px calc(66px + env(safe-area-inset-bottom)); }
.form-section {
  background: #fff; border-radius: 10px; padding: 4px 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}
.row { display: flex; align-items: center; padding: 10px 0; border-bottom: 1px solid #f5f5f5; }
.row:last-child { border-bottom: 0; }
.row label { width: 80px; color: #666; font-size: 14px; }
.row input, .row select { flex: 1; border: 0; font-size: 15px; outline: none; }
.perm-section { margin-top: 12px; background: #fff; border-radius: 10px; padding: 12px 14px; }
.section-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.section-head b { font-size: 14px; color: #333; }
.toggle { background: none; border: 0; color: #1989fa; font-size: 13px; }
.mini-loading, .mini-empty { color: #999; font-size: 13px; padding: 20px 0; text-align: center; }

/* —— 三级树样式 —— */
.perm-tree { display: flex; flex-direction: column; }
.mod-block { border-bottom: 1px solid #f0f0f0; }
.mod-block:last-child { border-bottom: 0; }

.mod-row {
  display: flex; align-items: center; gap: 8px; cursor: pointer;
  padding: 10px 4px; font-weight: 600; font-size: 15px; color: #1a1a1a;
}
.expand-arrow { color: #bbb; transition: transform .15s; width: 16px; display: inline-flex; justify-content: center; }
.expand-arrow.expanded { transform: rotate(90deg); }
.mod-check, .res-check {
  width: 18px; height: 18px; border-radius: 4px; border: 1.5px solid #ccc;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; background: #fff; color: #fff; font-size: 12px;
}
.mod-check :deep(.van-icon), .res-check :deep(.van-icon) { font-size: 12px; color: #fff; }
.mod-row:has(.van-icon-success) .mod-check { background: #1989fa; border-color: #1989fa; }
.res-row:has(.van-icon-success) .res-check { background: #1989fa; border-color: #1989fa; }
.indeterminate { display: inline-block; width: 10px; height: 2px; background: #1989fa; border-radius: 2px; }
.mod-row:has(.indeterminate) .mod-check { background: #1989fa; border-color: #1989fa; }
.res-row:has(.indeterminate) .res-check { background: #1989fa; border-color: #1989fa; }
.mod-label { flex: 1; }
.mod-count { color: #999; font-size: 12px; font-weight: 400; }

.resources { padding-left: 22px; padding-bottom: 4px; }
.res-block { border-top: 1px dashed #eee; }
.res-row {
  display: flex; align-items: center; gap: 8px; cursor: pointer;
  padding: 8px 4px; font-size: 14px; color: #333;
}
.res-label { flex: 1; color: #555; font-weight: 500; }
.res-count { color: #999; font-size: 12px; }

.actions {
  display: flex; flex-wrap: wrap; gap: 6px 10px;
  padding: 4px 0 10px 22px;
}
.action-item {
  display: flex; align-items: center; gap: 5px;
  padding: 5px 10px; border-radius: 14px; border: 1.5px solid #eee;
  background: #fafafa; cursor: pointer; font-size: 12px; color: #666;
  transition: all .12s;
}
.action-item.active { border-color: #1989fa; background: #e8f4ff; color: #1989fa; }
.act-check {
  width: 14px; height: 14px; border-radius: 3px; border: 1.5px solid #bbb;
  display: flex; align-items: center; justify-content: center;
  background: #fff; color: #fff; flex-shrink: 0;
}
.action-item.active .act-check { border-color: #1989fa; background: #1989fa; }
.action-item.active .act-check :deep(.van-icon) { font-size: 10px; color: #fff; }
.act-name { line-height: 1; }
</style>



// WEBPACK FOOTER //
// src/views/boss/identity/RoleEdit.vue